import React, { useState, useEffect, useCallback, memo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getRaffleWinners, RaffleWinner, getUserProfile } from '../config/api'

interface WinnerWithProfile {
  raffleId: string
  winner: {
    id: string
    discordId: string
  }
  completedAt: string
  participantCount: number
  scheduledAt: string
  // Discord profile data
  username?: string
  globalName?: string
  avatar?: string | null
}

const WinnersDisplay: React.FC = () => {
  const [winners, setWinners] = useState<WinnerWithProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Function to get Discord user info
  const getDiscordUserInfo = useCallback(async (discordId: string) => {
    try {
      // Try to get user profile from our API first
      try {
        const profile = await getUserProfile(discordId)
        return {
          username: profile.user.name || `User${discordId.slice(-4)}`,
          globalName: profile.user.display_name || undefined,
          avatar: profile.user.avatar_url || null // Используем avatar_url из нашего API
        }
      } catch (profileError) {
        // If profile not found, use fallback
        console.log(`Profile not found for user ${discordId}, using fallback`)
        return {
          username: `User${discordId.slice(-4)}`,
          globalName: undefined,
          avatar: null
        }
      }
    } catch (error) {
      console.error('Error fetching Discord user info:', error)
      return {
        username: `User${discordId.slice(-4)}`,
        globalName: undefined,
        avatar: null
      }
    }
  }, [])


  // Load winners data
  const loadWinners = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await getRaffleWinners()
      
      // Enrich winners with Discord profile data
      const enrichedWinners = await Promise.all(
        response.winners.map(async (winner: RaffleWinner) => {
          const discordInfo = await getDiscordUserInfo(winner.winner.discordId)
          return {
            ...winner,
            ...discordInfo
          }
        })
      )
      
      setWinners(enrichedWinners)
    } catch (err) {
      console.error('Error loading winners:', err)
      setError('Не удалось загрузить данные о победителях')
    } finally {
      setLoading(false)
    }
  }, [getDiscordUserInfo])

  // Load data on mount
  useEffect(() => {
    loadWinners()
  }, [loadWinners])

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(loadWinners, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [loadWinners])

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10 shadow-2xl"
      >
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-yellow-400"></div>
          <span className="ml-2 sm:ml-3 text-gray-300 text-sm sm:text-base">Загрузка победителей...</span>
        </div>
      </motion.div>
    )
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10 shadow-2xl"
      >
        <div className="text-center">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">⚠️</div>
          <h3 className="text-lg sm:text-xl font-bold text-red-400 mb-2">Ошибка загрузки</h3>
          <p className="text-gray-400 mb-4 text-sm sm:text-base">{error}</p>
          <button
            onClick={loadWinners}
            className="px-3 sm:px-4 py-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors text-sm sm:text-base"
          >
            Попробовать снова
          </button>
        </div>
      </motion.div>
    )
  }

  if (winners.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-dark-800/80 to-dark-900/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10 shadow-2xl"
      >
        <div className="text-center">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🏆</div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-300 mb-2">Победители розыгрышей</h3>
          <p className="text-gray-400 text-sm sm:text-base">Пока нет данных о победителях</p>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden bg-gradient-to-br from-dark-800/90 to-dark-900/90 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-white/20 shadow-2xl"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-4 sm:mb-6"
        >
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
            🏆 Победители прошлых недель
          </h3>
          <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-yellow-400 to-orange-400 mx-auto rounded-full"></div>
        </motion.div>

        {/* Winners List */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
          <AnimatePresence>
            {winners.map((winner, index) => {
              // Если avatar уже полный URL, используем его, иначе формируем default
              const avatarUrl = winner.avatar 
                ? winner.avatar // Используем уже готовый URL из API
                : `https://cdn.discordapp.com/embed/avatars/${parseInt(winner.winner.discordId) % 5}.png`

              return (
                <motion.div
                  key={winner.raffleId}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -8,
                    transition: { 
                      type: "spring", 
                      stiffness: 400, 
                      damping: 25 
                    }
                  }}
                  transition={{
                    delay: 0.3 + index * 0.15,
                    type: "spring",
                    stiffness: 200,
                    damping: 20
                  }}
                  // Мгновенный возврат при убирании мыши
                  layout={false}
                  className="relative flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 w-full sm:min-w-[200px] backdrop-blur-sm cursor-pointer group hover:shadow-2xl hover:shadow-yellow-500/20 hover:border-yellow-400/40"
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <motion.div 
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl overflow-hidden shadow-xl ring-2 ring-white/20 group-hover:ring-yellow-400/60 group-hover:shadow-yellow-400/30"
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        transition: { 
                          type: "spring", 
                          stiffness: 500, 
                          damping: 30 
                        }
                      }}
                      transition={{
                        duration: 0
                      }}
                    >
                      <img 
                        src={avatarUrl} 
                        alt={winner.username || 'Winner'} 
                        className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-50"
                        onError={(e) => {
                          // Если аватарка не загрузилась, показываем fallback с буквой
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.nextElementSibling?.classList.remove('hidden')
                        }}
                      />
                      <div className="w-full h-full rounded-lg sm:rounded-xl bg-gradient-to-br from-dark-700 to-dark-800 flex items-center justify-center text-lg sm:text-xl font-bold text-white hidden group-hover:from-yellow-600 group-hover:to-orange-600 transition-all duration-200">
                        {winner.username?.charAt(0).toUpperCase() || 'W'}
                      </div>
                    </motion.div>
                  </div>

                  {/* Winner Name */}
                  <div className="flex-1 relative z-10 min-w-0">
                    <div className="text-base sm:text-lg font-bold text-white group-hover:text-yellow-300 transition-colors duration-100 truncate">
                      {winner.globalName || winner.username || `User${winner.winner.discordId.slice(-4)}`}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400 group-hover:text-yellow-400 transition-colors duration-100">
                      Победитель #{winner.raffleId}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>

      </div>
    </motion.div>
  )
}

export default memo(WinnersDisplay)
